/**
 * Fetch wrapper compartido entre apps del frontend. Maneja:
 * - `credentials: 'include'` (mandatorio para cookies HttpOnly de auth)
 * - CSRF: lee cookie `{audiencePrefix}_csrf` y la agrega como header X-CSRF-Token
 *   en mutations (POST/PUT/PATCH/DELETE)
 * - Error mapping → ApiError con statusCode + body
 */

export interface ApiClientOptions {
  /** URL base — ej "/" (proxy) o "http://localhost:5000" (cross-origin dev) */
  baseUrl: string;
  /** Nombre de la cookie CSRF a leer. Cliente=cp_csrf, Recruiter=rec_csrf. */
  csrfCookieName: string;
  /** Header name para CSRF token. Convención: 'X-CSRF-Token'. */
  csrfHeaderName?: string;
  /**
   * Resolver del idioma actual del usuario (ej: `() => i18n.language`). Si retorna
   * un valor no vacío, se envía como header `X-User-Language` en cada request.
   *
   * Razón del header custom: `Accept-Language` es forbidden-header per fetch spec —
   * Firefox/WebKit overridean valores custom. El backend prioriza `X-User-Language`
   * sobre `Accept-Language` en su RequestLocalizationOptions.
   */
  getCurrentLanguage?: () => string | null | undefined;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `API error ${status}`);
    this.status = status;
    this.body = body;
  }
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return m ? decodeURIComponent(m[1]!) : null;
}

export function createApiClient(options: ApiClientOptions) {
  const csrfHeaderName = options.csrfHeaderName ?? 'X-CSRF-Token';

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const method = (init.method ?? 'GET').toUpperCase();
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');

    if (init.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    if (!SAFE_METHODS.has(method)) {
      const csrf = readCookie(options.csrfCookieName);
      if (csrf) {
        headers.set(csrfHeaderName, csrf);
      } else if (typeof console !== 'undefined') {
        // SW-137 diagnostic: mutating requests sin CSRF cookie son rejected por el BE
        // con 403. Esto típicamente pasa cuando el browser nunca recibió la cookie
        // (Set-Cookie bloqueado por config Amplify, o domain mismatch) o cuando la
        // sesión expiró y solo se mantiene cp_session HttpOnly.
        console.warn(
          `[api-client] CSRF cookie "${options.csrfCookieName}" no encontrada para ${method} ${path}. ` +
          `Esto causará 403 Forbidden. Visible cookies: ${typeof document !== 'undefined' ? document.cookie : 'n/a'}`,
        );
      }
    }

    const language = options.getCurrentLanguage?.();
    if (language && !headers.has('X-User-Language')) {
      headers.set('X-User-Language', language);
    }

    const url = path.startsWith('http') ? path : `${options.baseUrl.replace(/\/$/, '')}${path}`;
    const response = await fetch(url, {
      ...init,
      method,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      let body: unknown = null;
      try {
        body = await response.json();
      } catch {
        // ignore
      }
      throw new ApiError(response.status, body);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  return {
    get: <T>(path: string, init?: RequestInit) => request<T>(path, { ...init, method: 'GET' }),
    post: <T>(path: string, body?: unknown, init?: RequestInit) =>
      request<T>(path, { ...init, method: 'POST', body: body ? JSON.stringify(body) : undefined }),
    put: <T>(path: string, body?: unknown, init?: RequestInit) =>
      request<T>(path, { ...init, method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
    patch: <T>(path: string, body?: unknown, init?: RequestInit) =>
      request<T>(path, { ...init, method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
    delete: <T>(path: string, init?: RequestInit) => request<T>(path, { ...init, method: 'DELETE' }),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
